<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type { GalleryFile } from '#ui-shared/components/gallery/gallery.types';
import { getGalleryFileSource } from '#ui-shared/components/gallery/gallery.types';
import GalleryFileCard from '#ui-shared/components/gallery/GalleryFileCard.vue';
import { useMediaRetry } from '#ui-shared/composables/useMediaRetry';
import { isImage, isVideo } from '#ui-shared/utils/pretty-print-file-size';

const props = defineProps<{
  file: GalleryFile;
  refreshUrls?: () => void;
}>();

const emit = defineEmits<{
  open: [];
  close: [];
}>();

const { t } = useI18n();
const videoRef = ref<HTMLVideoElement | null>(null);
const source = computed(() => getGalleryFileSource(props.file));
const imageFailed = ref(false);

const { isError, handleError, handleSuccess } = useMediaRetry({
  urlRef: source,
  onRefreshRequest: props.refreshUrls,
  videoRef,
});

/**
 * Shows the file card right away while the image retries in the background.
 */
function onImageError(): void {
  imageFailed.value = true;
  handleError();
}

function onImageLoad(): void {
  imageFailed.value = false;
  handleSuccess();
}
</script>

<template>
  <div
    v-if="isImage(file.mimeType)"
    class="relative h-full flex items-center justify-center"
    @click.self="emit('close')"
  >
    <img
      v-show="!imageFailed"
      :src="source"
      alt=""
      class="max-w-[95%] max-h-[95%] bg-black"
      @click.stop
      @load="onImageLoad"
      @error="onImageError"
    >
    <GalleryFileCard
      v-if="imageFailed"
      :file="file"
      @open="emit('open')"
    />
  </div>

  <div
    v-else-if="isVideo(file.mimeType)"
    class="relative h-full flex items-center justify-center"
    @click.self="emit('close')"
  >
    <GalleryFileCard
      v-if="isError"
      :file="file"
      @open="emit('open')"
    />
    <video
      v-else
      ref="videoRef"
      controls
      autoplay
      class="max-h-[95%] max-w-[95%]"
      @click.stop
      @loadeddata="handleSuccess"
      @error="handleError"
    >
      <source :src="source">
      {{ t('error.videoNotSupported') }}
    </video>
  </div>

  <GalleryFileCard
    v-else
    :file="file"
    @open="emit('open')"
  />
</template>
